import { useState } from 'react';
import { CopyOutlined, DownloadOutlined } from '@ant-design/icons';

import { Badge, Button } from '../../../../components/ui';
import type { FieldControlProps } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { useToast } from '../../../../lib/toast';
import { ipThuocCidr } from '../../nghiep-vu/cidr';
import {
  cungDanhSach,
  danhSachCuaGiaTri,
  docDanhSachIp,
  sangDongCidr,
  vietDanhSachIp,
} from '../../nghiep-vu/danh-sach-ip';
import type { DongCidr } from '../../types';
import { ONhapDanhSachIp, cauLoiDong } from './ONhapDanhSachIp';

/** Trần mặc định của danh mục (`bounds.max_items` của `network.ip_allowlist`). */
const TRAN_MAC_DINH = 200;
/** `NOTE_MAX_LENGTH` của `SettingsValidator`. */
const GHI_CHU_TOI_DA = 200;
const TEN_TEP = 'dai-tin-cay.txt';

/**
 * Ô sửa dải tin cậy (`network.ip_allowlist`, ADR-0098): **cả** danh sách trong một ô nhập theo dòng, sửa tại chỗ
 * như aaPanel; lưu qua thanh lưu chung là thay cả danh sách.
 *
 * Bản nháp của form là mảng khi ô hợp lệ, **chuỗi nguyên văn** khi còn dòng sai (`danhSachCuaGiaTri`): form chặn lưu
 * và đổi thẻ không mất dòng đang sửa. Ô giữ đúng chữ người dùng gõ (dòng ghi chú, khoảng trắng) chừng nào nó còn đọc
 * ra đúng giá trị của form; giá trị đổi từ nơi khác (Hoàn tác, Về mặc định, thêm từ báo cáo IP) thì ô viết lại từ
 * giá trị đó.
 *
 * Không có hạn cho từng dòng: dải tin cậy chỉ miễn trần request, không chặn ai, nên dòng tạm không cần (ADR-0098).
 */
export function ODaiTinCay({
  giaTri,
  onDoi,
  chiDoc,
  p,
  ipHienTai,
  max,
  daLuu,
}: {
  giaTri: unknown;
  onDoi: (v: unknown) => void;
  chiDoc: boolean;
  p: FieldControlProps;
  ipHienTai: string | null;
  max: number | null | undefined;
  /** Giá trị đã lưu hiện tại (không phải lúc mở ô): gõ về đúng như nó thì bản nháp trả lại chính giá trị này. */
  daLuu?: unknown;
}) {
  const { t } = useT();
  const toast = useToast();
  const tran = max ?? TRAN_MAC_DINH;
  const tuyChon = { toiDa: tran, camTatCa: true, ghiChuToiDa: GHI_CHU_TOI_DA };
  /*
   * Gõ về đúng danh sách đã lưu thì trả lại chính giá trị đã lưu để form không đếm là thay đổi. So với bản đã lưu
   * **hiện tại**, không phải bản lúc mở ô: sau một lần lưu hay tải lại, bản lúc mở ô đã cũ. `cungDanhSach` coi ghi
   * chú rỗng và `null` như nhau — backend trả `null` cho dòng không ghi chú, ô nhập đọc ra chuỗi rỗng.
   */
  const goc: DongCidr[] = Array.isArray(daLuu) ? (daLuu as DongCidr[]) : [];
  const [daGo, setDaGo] = useState<string | null>(null);

  const ds = danhSachCuaGiaTri(giaTri, tuyChon);
  const van =
    typeof giaTri === 'string'
      ? giaTri
      : daGo !== null && ds !== null && cungDanhSach(sangDongCidr(docDanhSachIp(daGo, tuyChon).muc), ds)
        ? daGo
        : vietDanhSachIp(ds ?? []);
  const kq = docDanhSachIp(van, tuyChon);
  const loiDong = kq.loi.map((l) => ({
    dong: l.dong,
    cau: cauLoiDong(t, l, { max: tran, ghiChuToiDa: GHI_CHU_TOI_DA }),
  }));

  const doi = (v: string) => {
    setDaGo(v);
    const k = docDanhSachIp(v, tuyChon);
    if (k.loi.length > 0) {
      onDoi(v);
      return;
    }
    const moi = sangDongCidr(k.muc);
    onDoi(cungDanhSach(moi, goc) ? goc : moi);
  };

  const noiDungXuat = () => `${vietDanhSachIp(ds ?? sangDongCidr(kq.muc))}\n`;

  const saoChep = async () => {
    try {
      await navigator.clipboard.writeText(noiDungXuat());
      toast.success(t('baoMat.daiTinCay.daSaoChep', { so: kq.muc.length }));
    } catch {
      toast.error(t('baoMat.daiTinCay.khongSaoChepDuoc'));
    }
  };

  const taiVe = () => {
    const url = URL.createObjectURL(new Blob([noiDungXuat()], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = TEN_TEP;
    a.click();
    URL.revokeObjectURL(url);
  };

  const trongDai = ipHienTai && kq.muc.some((m) => ipThuocCidr(ipHienTai, m.cidr));

  return (
    <ONhapDanhSachIp
      id={p.id}
      moTaId={p['aria-describedby']}
      giaTri={van}
      onDoi={doi}
      chiDoc={chiDoc}
      kq={kq}
      loiDong={loiDong}
      max={tran}
      viDu={t('baoMat.daiTinCay.viDu')}
      huongDan={[t('baoMat.daiTinCay.huongDanThayCa'), t('baoMat.daiTinCay.huongDanDan')]}
      dauTren={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="flex min-w-0 flex-wrap items-center gap-2 text-xs text-content-muted">
            {ipHienTai && (
              <>
                <span>
                  {t('baoMat.ip.ipCuaBan')} <span className="font-mono text-content-primary">{ipHienTai}</span>
                </span>
                <Badge variant={trongDai ? 'success' : 'neutral'}>
                  {t(trongDai ? 'baoMat.daiTinCay.ipTrongDai' : 'baoMat.daiTinCay.ipNgoaiDai')}
                </Badge>
              </>
            )}
          </span>
          <span className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              icon={<CopyOutlined />}
              disabled={kq.muc.length === 0}
              onClick={() => void saoChep()}
            >
              {t('baoMat.daiTinCay.saoChep')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              icon={<DownloadOutlined />}
              disabled={kq.muc.length === 0}
              onClick={taiVe}
            >
              {t('baoMat.daiTinCay.taiVe')}
            </Button>
          </span>
        </div>
      }
    />
  );
}
